import { useEffect, useMemo, useState } from 'react';
import { buildStateCountyLocatorMapModel } from './StateCountyLocatorMap';
import { SvgLocationPin } from './MapLocationPin';
import { apiJson } from '../services/api';
import MapSourceAuditDropdown from './MapSourceAuditDropdown';
import PageLoader from './PageLoader';
import './FemaNriRegionMapSection.css';

const DIMENSIONS = { width: 860, height: 380, padding: 18 };
const QUICK_HAZARD_MATCHES = [
  { key: 'overall', label: 'Overall' },
  { match: 'inland flood', label: 'Flood' },
  { match: 'wildfire', label: 'Wildfire' },
  { match: 'tornado', label: 'Tornado' },
  { match: 'hail', label: 'Hail' },
];
const RISK_COLORS = ['#dff3f4', '#b9e2e4', '#87cfd1', '#62bec1', '#3a9fa2'];
const MISSING_FILL = '#f8fafc';
const FEMA_REGION_MAP_AUDIT_KEYS = ['fema_nri_counties'];

function finiteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function fillForCounty(county) {
  if (!county || county.source_state !== 'applicable_with_value') return MISSING_FILL;
  const score = finiteNumber(county.risk_score);
  if (score == null) return MISSING_FILL;
  if (score >= 80) return RISK_COLORS[4];
  if (score >= 60) return RISK_COLORS[3];
  if (score >= 40) return RISK_COLORS[2];
  if (score >= 20) return RISK_COLORS[1];
  return RISK_COLORS[0];
}

function formatScore(value) {
  const number = finiteNumber(value);
  return number == null ? '—' : number.toFixed(1);
}

function formatCurrency(value) {
  const number = finiteNumber(value);
  if (number == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(number);
}

function formatPercent(value) {
  const number = finiteNumber(value);
  return number == null ? '—' : `${Math.round(number * 100)}%`;
}

function TopHazardDrivers({ county }) {
  if (!county?.top_hazards?.length) return null;
  return (
    <div className="fema-region-map__drivers">
      <small>Top loss drivers</small>
      {county.top_hazards.map((hazard) => (
        <div key={hazard.key}>
          <strong>{hazard.label}</strong>
          <span>{formatCurrency(hazard.expected_annual_loss)} · {formatPercent(hazard.loss_share)}</span>
        </div>
      ))}
    </div>
  );
}

function buildQuickHazards(hazards) {
  return QUICK_HAZARD_MATCHES.map((item) => {
    if (item.key === 'overall') return item;
    const hazard = hazards.find((candidate) => String(candidate?.label || '').toLowerCase().includes(item.match));
    return hazard ? { key: hazard.key, label: item.label } : null;
  }).filter(Boolean);
}

export default function FemaNriRegionMapSection({ initialPayload, loading, error, market }) {
  const [payload, setPayload] = useState(initialPayload);
  const [selectedHazard, setSelectedHazard] = useState(initialPayload?.selected_hazard?.key || 'overall');
  const [activeCounty, setActiveCounty] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [hazardError, setHazardError] = useState('');
  const [overallCounties, setOverallCounties] = useState(
    initialPayload?.selected_hazard?.key === 'overall' ? initialPayload.counties || [] : [],
  );
  const [activeMarker, setActiveMarker] = useState(false);

  useEffect(() => {
    if (!initialPayload) return;
    setPayload(initialPayload);
    setSelectedHazard(initialPayload.selected_hazard?.key || 'overall');
    if (initialPayload.selected_hazard?.key === 'overall') setOverallCounties(initialPayload.counties || []);
  }, [initialPayload]);

  const model = useMemo(() => buildStateCountyLocatorMapModel(
    market || payload?.market,
    payload,
    DIMENSIONS,
  ), [market, payload]);
  const countyByFips = useMemo(() => new Map((payload?.counties || []).map((county) => [county.geo_id, county])), [payload?.counties]);
  const overallCountyByFips = useMemo(() => new Map(overallCounties.map((county) => [county.geo_id, county])), [overallCounties]);
  const marketCountyFips = useMemo(() => new Set((payload?.market_counties || []).map((county) => county.county_fips)), [payload?.market_counties]);
  const hazards = payload?.hazards || [];
  const quickHazards = buildQuickHazards(hazards);
  const quickKeys = new Set(quickHazards.map((hazard) => hazard.key));

  async function selectHazard(nextKey) {
    if (!nextKey || nextKey === selectedHazard) return;
    setSelectedHazard(nextKey);
    setSwitching(true);
    setHazardError('');
    setActiveCounty(null);
    try {
      const nextPayload = await apiJson(`/research/markets/${payload.market.slug}/fema-nri-map?hazard=${encodeURIComponent(nextKey)}`);
      if (nextPayload.selected_hazard?.key === 'overall') setOverallCounties(nextPayload.counties || []);
      setPayload(nextPayload);
    } catch (requestError) {
      setHazardError(requestError?.message || 'Could not load that FEMA hazard.');
    } finally {
      setSwitching(false);
    }
  }

  if (loading || (!initialPayload && !error)) {
    return (
      <section className="fema-region-map fema-region-map--state">
        <PageLoader compact label="Loading FEMA region map" />
      </section>
    );
  }
  if (error || !payload || !model) return <section className="fema-region-map fema-region-map--state">{error || 'FEMA region map is unavailable.'}</section>;

  const stateName = market?.state_name_full || payload.state?.name || payload.state?.state_abbr || 'State';
  const marketName = market?.region_name || payload.market?.name || 'Selected market';
  const markerCounty = overallCountyByFips.get(payload.market_counties?.[0]?.county_fips) || null;
  const legendItems = [
    [RISK_COLORS[4], 'Very high · 80–100'],
    [RISK_COLORS[3], 'High · 60–79.9'],
    [RISK_COLORS[2], 'Moderate · 40–59.9'],
    [RISK_COLORS[1], 'Low–moderate · 20–39.9'],
    [RISK_COLORS[0], 'Low · below 20'],
    [MISSING_FILL, 'No value / not applicable'],
  ];

  return (
    <section className="fema-region-map">
      <header className="fema-region-map__header">
        <div><p>FEMA NRI · County evidence</p><h2>FEMA Hazard Map | {stateName} County Context</h2><span>{marketName}’s governed counties stay outlined for region context.</span></div>
        <div className="fema-region-map__pills"><b>{payload.release?.display_version || 'Current release'}</b><b>{payload.county_count} counties</b></div>
      </header>
      <div className="fema-region-map__controls" aria-label="FEMA hazard selector">
        {quickHazards.map((hazard) => <button key={hazard.key} type="button" className={selectedHazard === hazard.key ? 'is-active' : ''} aria-pressed={selectedHazard === hazard.key} onClick={() => selectHazard(hazard.key)}>{hazard.label}</button>)}
        <label><span>More hazards</span><select value={quickKeys.has(selectedHazard) ? '' : selectedHazard} onChange={(event) => selectHazard(event.target.value)}><option value="">Select hazard</option>{hazards.filter((hazard) => !quickKeys.has(hazard.key)).map((hazard) => <option key={hazard.key} value={hazard.key}>{hazard.label}</option>)}</select></label>
      </div>
      {hazardError ? <p className="fema-region-map__error">{hazardError}</p> : null}
      <div className={`fema-region-map__body ${switching ? 'is-loading' : ''}`}>
        <div className="fema-region-map__stage">
          <svg viewBox={`0 0 ${model.width} ${model.height}`} role="img" aria-label={`FEMA ${payload.selected_hazard?.label || 'overall risk'} map for ${stateName}`} onMouseLeave={() => { setActiveCounty(null); setActiveMarker(false); }}>
            <rect width={model.width} height={model.height} className="fema-region-map__background" />
            {model.countyPaths.map((shape) => {
              const county = countyByFips.get(shape.geoId);
              return <path key={shape.geoId} d={shape.d} fill={fillForCounty(county)} className={`fema-region-map__county${marketCountyFips.has(shape.geoId) ? ' is-market-county' : ''}`} tabIndex="0" aria-label={county ? `${county.name}, ${county.state_abbr}` : `County ${shape.geoId}`} onMouseEnter={() => { setActiveMarker(false); setActiveCounty(county || null); }} onMouseLeave={() => setActiveCounty(null)} onFocus={() => { setActiveMarker(false); setActiveCounty(county || null); }} onBlur={() => setActiveCounty(null)} />;
            })}
            {model.outlinePath ? <path d={model.outlinePath} className="fema-region-map__outline" /> : null}
            {model.pin ? <g tabIndex="0" role="button" aria-label={`Market location for ${marketName}`} onMouseEnter={() => { setActiveMarker(true); setActiveCounty(null); }} onMouseLeave={() => setActiveMarker(false)} onFocus={() => { setActiveMarker(true); setActiveCounty(null); }} onBlur={() => setActiveMarker(false)}><SvgLocationPin pin={{ ...model.pin, ariaLabel: '' }} scale={0.82} className="fema-region-map__pin" /></g> : null}
          </svg>
          {activeMarker && markerCounty ? <div className="fema-region-map__tooltip"><strong>{markerCounty.name}, {markerCounty.state_abbr}</strong><b>{markerCounty.risk_rating || 'No rating'} · {formatScore(markerCounty.risk_score)}</b><TopHazardDrivers county={markerCounty} /></div> : activeCounty ? <div className="fema-region-map__tooltip"><strong>{activeCounty.name}, {activeCounty.state_abbr}</strong><b>{activeCounty.risk_rating || 'No rating'} · {formatScore(activeCounty.risk_score)}</b>{selectedHazard === 'overall' && activeCounty.top_hazards?.length ? <TopHazardDrivers county={activeCounty} /> : <small>{formatCurrency(activeCounty.expected_annual_loss)} modeled annual loss</small>}</div> : null}
        </div>
        <aside className="fema-region-map__legend"><h3>{payload.selected_hazard?.label || 'Overall risk'} score</h3><div>{legendItems.map(([color, label]) => <span key={label}><i style={{ backgroundColor: color }} />{label}</span>)}</div><p>Relative modeled county risk—not a property-level determination.</p><a href={payload.source?.documentation_url || payload.source?.source_url} target="_blank" rel="noreferrer">FEMA NRI docs</a></aside>
      </div>
      <MapSourceAuditDropdown datasetKeys={FEMA_REGION_MAP_AUDIT_KEYS} />
    </section>
  );
}
